"use client";

import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ReviewsIcon from "@mui/icons-material/Reviews";
import { Box, Dialog, IconButton, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { handleOpenSnackBar } from "../../../../utils";
import DialogHeader from "../../../../components/Dialog/Header";
import DialogBody from "../../../../components/Dialog/Body";
import DialogActionButtons from "../../../../components/Dialog/Footer";
import { CURRENCY } from "../../../../constants/Variables";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing("24px"),
  },
  "& .MuiPaper-root": {
    width: "400px",
    [theme.breakpoints.down("sm")]: {
      margin: 12,
    },
  },
  "& .MuiTableContainer-root .MuiTable-root .MuiTableHead-root": {
    backgroundColor: "#f3f3f3",
  },
  "& .MuiTableCell-root ": {
    padding: "8px 16px",
  },
}));

const getFirstAvailableVariant = (product) => {
  if (
    !product?.isVariant ||
    !Array.isArray(product?.variants) ||
    product.variants.length === 0
  )
    return null;
  return (
    product.variants.find(
      (v) => v?.status === true && (v?.quantity ?? 0) > 0,
    ) ?? null
  );
};

const isVariantProduct = (p) =>
  !!(p?.isVariant && Array.isArray(p?.variants) && p.variants.length > 0);

const getVariantId = (v) => (v && v._id) || "";

const optId = (o) => (o && o._id) ?? "";
const attrId = (a) => (a && a._id) ?? "";

const optionMatchesVariantAttr = (opt, variantAttr) => {
  if (!variantAttr) return false;
  if (!opt) return false;
  return (
    (variantAttr.value !== undefined && variantAttr.value === opt.value) ||
    attrId(variantAttr) === optId(opt)
  );
};

const variantMatchesOptions = (variant, attributeOptionsMap) => {
  if (!variant?.attributes) return false;
  return Object.keys(attributeOptionsMap).every((key) =>
    optionMatchesVariantAttr(attributeOptionsMap[key], variant.attributes[key]),
  );
};

const findVariantForOption = (
  variants,
  variantAttributes,
  currentVariant,
  thisAttr,
  thisOpt,
) => {
  const inStock = (v) => v?.status === true && (v?.quantity ?? 0) > 0;
  if (!variants?.length || !thisAttr?.value) return null;

  const otherAttrs = (variantAttributes || []).filter(
    (a) => a.value !== thisAttr.value,
  );
  const desiredMap = { [thisAttr.value]: thisOpt };
  if (currentVariant?.attributes) {
    otherAttrs.forEach((a) => {
      const val = currentVariant.attributes[a.value];
      if (val) desiredMap[a.value] = val;
    });
  }

  return (
    variants.find((v) => variantMatchesOptions(v, desiredMap) && inStock(v)) ??
    null
  );
};

export const BasePackageSelectionDialog = forwardRef(
  ({ selectedProducts, setSelectedProducts }, ref) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [dialogData, setDialogData] = useState(null);
    const [productDetail, setProductDetail] = useState(null);
    const [selectedQty, setSelectedQty] = useState(1);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [disableCounters, setDisAbleCounters] = useState({
      add: false,
      subtract: true,
    });

    // Redux state
    const { currentLanguage } = useSelector(
      (state) => state.translationReducer,
    );

    useImperativeHandle(ref, () => ({
      openDialog(params) {
        const product = params?.product;
        setDialogData(params);
        setProductDetail(product);
        const first = getFirstAvailableVariant(product);
        setSelectedVariant(first);
        setOpen(true);
      },
      closeDialog() {
        handleClose();
      },
    }));

    const handleClose = () => {
      setOpen(false);
      setSelectedQty(1);
      setProductDetail(null);
      setSelectedVariant(null);
      setDisAbleCounters({
        add: false,
        subtract: true,
      });
      setDialogData(null);
    };

    const isVariant = isVariantProduct(productDetail);
    const effective =
      isVariant && selectedVariant ? selectedVariant : productDetail;

    const isDiscount = (effective?.discount ?? 0) > 0;
    const pprice = effective?.finalPrice ?? 0;
    const ppriceWithDiscount =
      (effective?.finalPrice ?? 0) + (effective?.discount ?? 0);
    const maxQty = effective?.quantity ?? 0;

    const handleDecreaseQty = () => {
      if (selectedQty <= 1) {
        return setDisAbleCounters((pre) => ({ ...pre, subtract: true }));
      }
      setSelectedQty((pre) => pre - 1);
      setDisAbleCounters((pre) => ({ ...pre, add: false }));
    };

    const handleIncreaseQty = () => {
      if (maxQty <= 0) return;
      if (selectedQty >= maxQty) {
        setDisAbleCounters((pre) => ({ ...pre, add: true }));
      } else {
        setDisAbleCounters((pre) => ({ ...pre, subtract: false }));
        setSelectedQty((pre) => pre + 1);
      }
    };

    const handleVariantChange = (variant) => {
      setSelectedVariant(variant);
      setSelectedQty(1);
      setDisAbleCounters({ add: false, subtract: true });
    };

    const handleAddProduct = () => {
      if (!productDetail || maxQty <= 0) return;

      const variantId = isVariant ? getVariantId(selectedVariant) : "";
      const tempCart = [...selectedProducts];
      const itemVariantId = (item) =>
        item.variantId || getVariantId(item.selectedVariant) || "";
      const existingIndex = tempCart.findIndex(
        (item) =>
          item._id === productDetail._id && itemVariantId(item) === variantId,
      );

      const cartItem = {
        ...productDetail,
        selectedQty,
        ...(isVariant && selectedVariant
          ? {
              selectedVariant,
              variantId: selectedVariant._id,
              price: selectedVariant.price ?? 0,
              profitMargin: selectedVariant.profitMargin ?? 0,
              discount: selectedVariant.discount ?? 0,
              finalPrice: selectedVariant.finalPrice ?? 0,
              quantity: selectedVariant.quantity ?? 0,
              coverImage:
                selectedVariant.images?.[0] || productDetail.coverImage,
            }
          : {}),
      };

      if (existingIndex !== -1) {
        tempCart[existingIndex] = { ...tempCart[existingIndex], selectedQty };
      } else {
        tempCart.push(cartItem);
      }

      handleOpenSnackBar("success", t("success.productAddedToCart"));
      setSelectedProducts(tempCart);
      handleClose();
    };

    return (
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <DialogHeader
          title={t("common.productQuickView")}
          secondaryHeading={t("common.selectOptionAndQuantity")}
          icon={
            <ReviewsIcon sx={{ fontSize: "2rem", color: "primary.main" }} />
          }
          onClose={handleClose}
        />

        <DialogBody>
          {isVariant && productDetail?.variantAttributes?.length > 0 && (
            <Stack spacing={1} mb={2}>
              {productDetail.variantAttributes.map((attr) => (
                <Stack
                  key={attr.value || attr._id}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  flexWrap="wrap"
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ minWidth: 56 }}
                  >
                    {attr?.label[currentLanguage] || attr?.label}
                    {attr?.sizeType ? ` (${attr?.sizeType})` : ""}:
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {(attr.values || []).map((opt) => {
                      const currentAttrVal =
                        selectedVariant?.attributes?.[attr.value];
                      const isSelected = optionMatchesVariantAttr(
                        opt,
                        currentAttrVal,
                      );
                      const variantForOpt = findVariantForOption(
                        productDetail.variants,
                        productDetail.variantAttributes,
                        selectedVariant,
                        attr,
                        opt,
                      );
                      const isDisabled = !variantForOpt;

                      return (
                        <IconButton
                          key={optId(opt) || opt.value}
                          size="small"
                          disabled={isDisabled}
                          onClick={() =>
                            variantForOpt && handleVariantChange(variantForOpt)
                          }
                          sx={{
                            border: "2px solid",
                            borderColor: isSelected
                              ? "primary.main"
                              : "divider",
                            borderRadius: 1,
                            bgcolor:
                              opt.value && /^#/.test(String(opt.value))
                                ? opt.value
                                : "grey.100",
                            color:
                              opt.value && /^#/.test(String(opt.value))
                                ? "transparent"
                                : "text.primary",
                            minWidth: 32,
                            minHeight: 32,
                            "&:hover:not(.Mui-disabled)": {
                              borderColor: "primary.main",
                              bgcolor:
                                opt.value && /^#/.test(String(opt.value))
                                  ? opt.value
                                  : "grey.200",
                            },
                          }}
                          aria-label={opt.label[currentLanguage] || opt.label}
                        >
                          {opt.value && /^#/.test(String(opt.value)) ? null : (
                            <Typography variant="caption">
                              {opt.label[currentLanguage] || opt.label}
                            </Typography>
                          )}
                        </IconButton>
                      );
                    })}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          )}

          <Box pb={2} display="flex" justifyContent="center">
            <Stack
              direction="row"
              alignItems="stretch"
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                overflow: "hidden",
                bgcolor: "grey.50",
              }}
            >
              <IconButton
                size="small"
                onClick={handleDecreaseQty}
                disabled={disableCounters.subtract}
                sx={{
                  borderRadius: 0,
                  px: 1.5,
                  "&:hover:not(.Mui-disabled)": { bgcolor: "grey.200" },
                  "&.Mui-disabled": { color: "action.disabled" },
                }}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
              <Box
                sx={{
                  minWidth: 48,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderLeft: "1px solid",
                  borderRight: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                }}
              >
                <Typography variant="body1" fontWeight={600}>
                  {selectedQty}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={handleIncreaseQty}
                disabled={disableCounters.add}
                sx={{
                  borderRadius: 0,
                  px: 1.5,
                  "&:hover:not(.Mui-disabled)": { bgcolor: "grey.200" },
                  "&.Mui-disabled": { color: "action.disabled" },
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            gap={1}
          >
            <Typography variant="h6" fontWeight={500}>
              {`${pprice} ${CURRENCY}`}
            </Typography>
            {isDiscount && (
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ textDecoration: "line-through" }}
              >
                {`${ppriceWithDiscount} ${CURRENCY}`}
              </Typography>
            )}
          </Stack>
        </DialogBody>
        <DialogActionButtons
          onCancel={handleClose}
          confirmText="add"
          onConfirm={handleAddProduct}
          isConfirmBtnDisable={maxQty <= 0 || (isVariant && !selectedVariant)}
        />
      </BootstrapDialog>
    );
  },
);
